import React, { useMemo } from 'react';
import { format as formatDate, eachDayOfInterval } from 'date-fns';
import { query, collection, where, documentId } from 'firebase/firestore';
import { sum, sumBy, groupBy, keyBy, chunk, zip } from 'lodash';

import useCollectionsFetch from '../hooks/useCollectionsFetch';
import MallBadge from '../MallBadge';
import { columnDefinitions, renderKpiCells, dailyColumnKeys } from '../productKpiColumns';
import { unslash } from '../../shared/util';
import { resolveUnitCostAt, shippingQuantityOf } from '../../shared/unitCostHistory';
import { getMallDefinition } from '../../shared/models/mallDefinitions';

export default function DailyProductKpiModal({ product, tenant, dateRange, visibleColumns }) {
  const [startDate, endDate] = dateRange;

  // 集計対象のSKU群
  // - Amazon親: 子SKU群(familySkus)
  // - 子/単品: 自分自身のSKU
  const mallDef = getMallDefinition(product.mall);
  // 売上 values のキーが productId(docID) 体系のモール（楽天・Shopify・Yahoo）
  const usesProductIdKey = mallDef.salesValuesKey === 'productId';
  const isParent = product.amazonAttributes?.isParent
    || product.rakutenAttributes?.isParent
    || product.shopifyAttributes?.isParent
    || product.yahooAttributes?.isParent;
  const familySkus = product[mallDef.attributesKey]?.familySkus;
  const targetSkus = (isParent && familySkus?.length) ? familySkus : [product.sku];
  const ownerId = product.sellerId || product.shopId;
  // 楽天は売上/広告の values キーが productId(docID=shopId__manageNumber__variantId) なので docID で引く。
  //   - 親: 子docID群（親product.id = shopId__manageNumber に variantId を足す）
  //   - 子/単品: 自身の product.id
  // Amazon は従来どおり 売上=sku、広告=`${ownerId}__${sku}`。
  // Shopify も同じ docID 体系（親 product.id に variantGid を足すと子 docID になる）。
  // Yahoo も同様（親 product.id = shopId__itemCode に subCode を足すと子 docID になる）
  const targetSalesKeys = usesProductIdKey
    ? ((isParent && familySkus?.length) ? familySkus.map(vid => `${product.id}__${unslash(vid)}`) : [product.id])
    : targetSkus;
  const targetProductIds = usesProductIdKey
    ? targetSalesKeys
    : targetSkus.map(sku => `${ownerId}__${sku}`);

  // 渡される product は商品一覧の集計結果で、unitCost が期間の実効平均単価に置き換わっているため原価計算には使えない。
  // 対象SKUの商品docを直接引き、SKU×日付ごとに unitCostHistory を解決する
  const targetProductDocIds = targetProductIds.map(unslash);
  const targetProducts = useCollectionsFetch(
    chunk(targetProductDocIds, 10).map(ids => query(collection(tenant.ref, 'products'), where(documentId(), 'in', ids))),
    [targetProductDocIds.join(',')]
  );
  const targetProductsById = keyBy(targetProducts, 'id');
  const costTargets = zip(targetSalesKeys, targetProductDocIds).map(([salesKey, productId]) => ({ salesKey, product: targetProductsById[productId] }));
  const singleTargetProduct = costTargets.length === 1 ? costTargets[0].product : null;

  const salesCollectionName = mallDef.productsSalesCollection;

  const salesData = useCollectionsFetch(
    [
      query(
        collection(tenant.ref, salesCollectionName),
        where('date', '>=', formatDate(startDate, 'yyyy-MM-dd')),
        where('date', '<=', formatDate(endDate, 'yyyy-MM-dd')),
      )
    ],
    [product.id, startDate, endDate]
  );

  // 広告KPIチャンク: 楽天=rakutenProductsAdKpis、Amazon=amazonProductsAdKpis。values キーは両方 productId(docID)。
  // 広告未対応モール(Shopify)は null。その場合はクエリを投げない
  const adCollectionName = mallDef.productsAdKpisCollection;
  const adData = useCollectionsFetch(
    adCollectionName == null ? [] : [
      query(
        collection(tenant.ref, adCollectionName),
        where('date', '>=', formatDate(startDate, 'yyyy-MM-dd')),
        where('date', '<=', formatDate(endDate, 'yyyy-MM-dd')),
      )
    ],
    [product.id, startDate, endDate]
  );

  // メインテーブルの表示列のうち、日次に馴染む列のみを表示
  const dailyVisibleColumns = useMemo(
    _ => dailyColumnKeys.filter(_ => !visibleColumns || visibleColumns.includes(_)),
    [visibleColumns]
  );

  const dailyKpis = useMemo(_ => {
    const salesByDate = groupBy(salesData, 'date');
    const adByDate = groupBy(adData, 'date');

    const dates = eachDayOfInterval({ start: startDate, end: endDate })
      .map(_ => formatDate(_, 'yyyy-MM-dd'))
      .reverse();

    // 1日分のドキュメント群から、対象キーのvaluesを合算
    const sumValues = (docs, ids, key) => sumBy(docs, doc =>
      sumBy(ids, id => doc.values?.[id]?.[key] || 0)
    );

    return dates.map(date => {
      const dateSalesData = salesByDate[date] || [];
      const dateAdData = adByDate[date] || [];

      // computeProductKpis と同じ集計式
      const sales = sumValues(dateSalesData, targetSalesKeys, 'sales');
      const refund = sumValues(dateSalesData, targetSalesKeys, 'refund');
      const salesFees = sumValues(dateSalesData, targetSalesKeys, 'commission');
      const fbaFees = sumValues(dateSalesData, targetSalesKeys, 'fbaFee');
      const quantity = sumValues(dateSalesData, targetSalesKeys, 'quantity');
      const refundQuantity = sumValues(dateSalesData, targetSalesKeys, 'refundQuantity');
      const systemUsageFee = sumValues(dateSalesData, targetSalesKeys, 'systemUsageFee');
      const safetyFee = sumValues(dateSalesData, targetSalesKeys, 'safetyFee');
      const paymentFee = sumValues(dateSalesData, targetSalesKeys, 'paymentFee');
      const pointsBaseCost = sumValues(dateSalesData, targetSalesKeys, 'pointsBaseCost');
      const pointsCost = sumValues(dateSalesData, targetSalesKeys, 'pointsCost');
      const promotionCost = sumValues(dateSalesData, targetSalesKeys, 'promotionCost');
      const vineQuantity = sumValues(dateSalesData, targetSalesKeys, 'vineQuantity');
      const vineSales = sumValues(dateSalesData, targetSalesKeys, 'vineSales');
      const adCost = sumValues(dateAdData, targetProductIds, 'cost');
      // 楽天RPPの広告売上は sales、Amazonは sales1d
      const adSales = sumValues(dateAdData, targetProductIds, mallDef.adSalesField);

      const netQuantity = quantity - refundQuantity - vineQuantity;
      const cost = sumBy(costTargets, ({ salesKey, product: targetProduct }) => {
        const targetNetQuantity = sumValues(dateSalesData, [salesKey], 'quantity')
          - sumValues(dateSalesData, [salesKey], 'refundQuantity')
          - sumValues(dateSalesData, [salesKey], 'vineQuantity');
        return resolveUnitCostAt(targetProduct, date) * targetNetQuantity;
      });
      const dailyUnitCost = netQuantity > 0
        ? Math.round(cost / netQuantity)
        : (singleTargetProduct ? resolveUnitCostAt(singleTargetProduct, date) : null);
      const fbaShippingCost = Math.abs(fbaFees);
      const shippingQuantities = costTargets.map(({ salesKey }) => sumBy(dateSalesData, doc => shippingQuantityOf(doc.values?.[salesKey])));
      const shippingQuantity = sum(shippingQuantities);
      const shippingCost = sum(costTargets.map((_, i) => (_.product?.unitShippingCost || 0) * shippingQuantities[i]));
      const dailyUnitShippingCost = shippingQuantity > 0
        ? Math.round(shippingCost / shippingQuantity)
        : (singleTargetProduct?.unitShippingCost || null);

      const netSales = sales + refund - vineSales;
      // ポイント原資(絶対負担1%)は原価2扱いなので純粗利益から控除。pointsBaseCostは負値
      const grossProfit = netSales - cost + salesFees + systemUsageFee + safetyFee + paymentFee - fbaShippingCost - shippingCost + pointsBaseCost;
      const contributionMargin = grossProfit - adCost + pointsCost + promotionCost;
      const organicSales = netSales - adSales;

      return {
        date,
        dailyUnitCost,
        dailyUnitShippingCost,
        shippingQuantity,
        kpis: {
          grossSales: sales,
          refund: Math.abs(refund),
          vineSales,
          sales: netSales,
          grossQuantity: quantity,
          refundQuantity,
          vineQuantity,
          quantity: netQuantity,
          cost,
          salesFees: Math.abs(salesFees),
          systemUsageFee: Math.abs(systemUsageFee),
          safetyFee: Math.abs(safetyFee),
          paymentFee: Math.abs(paymentFee),
          pointsBaseCost: Math.abs(pointsBaseCost),
          fbaShippingCost,
          shippingCost,
          grossProfit,
          grossProfitRate: netSales > 0 ? grossProfit / netSales : 0,
          adCost,
          promotionCost,
          pointsCost,
          contributionMargin,
          contributionMarginRate: netSales > 0 ? contributionMargin / netSales : 0,
          adSales,
          adSalesRatio: netSales > 0 ? adSales / netSales : 0,
          organicSales,
          organicSalesRatio: netSales > 0 ? organicSales / netSales : 0,
          acos: adSales > 0 ? adCost / adSales : 0,
          acots: netSales > 0 ? adCost / netSales : 0,
        }
      };
    });
  }, [salesData, adData, targetProducts, startDate, endDate, targetSalesKeys.join(','), targetProductIds.join(',')]);

  // 合計行: 加算可能な項目は合算し、率は合算値から再計算
  const totals = useMemo(_ => {
    const total = key => sumBy(dailyKpis, _ => _.kpis[key] || 0);
    const grossSales = total('grossSales');
    const vineSales = total('vineSales');
    const refund = total('refund');
    const netSales = grossSales - refund - vineSales;
    const grossProfit = total('grossProfit');
    const adCost = total('adCost');
    const adSales = total('adSales');
    const contributionMargin = total('contributionMargin');
    const organicSales = total('organicSales');
    const shippingQuantity = sumBy(dailyKpis, 'shippingQuantity');
    return {
      unitCost: total('quantity') > 0 ? Math.round(total('cost') / total('quantity')) : singleTargetProduct?.unitCost,
      unitShippingCost: shippingQuantity > 0 ? Math.round(total('shippingCost') / shippingQuantity) : singleTargetProduct?.unitShippingCost,
      grossSales,
      refund,
      vineSales,
      sales: netSales,
      grossQuantity: total('grossQuantity'),
      refundQuantity: total('refundQuantity'),
      vineQuantity: total('vineQuantity'),
      quantity: total('quantity'),
      cost: total('cost'),
      salesFees: total('salesFees'),
      systemUsageFee: total('systemUsageFee'),
      safetyFee: total('safetyFee'),
      paymentFee: total('paymentFee'),
      pointsBaseCost: total('pointsBaseCost'),
      fbaShippingCost: total('fbaShippingCost'),
      shippingCost: total('shippingCost'),
      grossProfit,
      grossProfitRate: netSales > 0 ? grossProfit / netSales : 0,
      adCost,
      promotionCost: total('promotionCost'),
      pointsCost: total('pointsCost'),
      contributionMargin,
      contributionMarginRate: netSales > 0 ? contributionMargin / netSales : 0,
      adSales,
      adSalesRatio: netSales > 0 ? adSales / netSales : 0,
      organicSales,
      organicSalesRatio: netSales > 0 ? organicSales / netSales : 0,
      acos: adSales > 0 ? adCost / adSales : 0,
      acots: netSales > 0 ? adCost / netSales : 0,
    };
  }, [dailyKpis]);

  const imageUrl = product.amazonAttributes?.summaries?.[0]?.mainImage?.link;
  const visibleColumnDefs = columnDefinitions.filter(_ => dailyVisibleColumns.includes(_.key));

  return (
    <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
      <div className="mb-3 d-flex gap-3">
        {imageUrl && (
          <img src={imageUrl} style={{ maxHeight: 80, maxWidth: 80 }} alt="商品画像" />
        )}
        <div>
          <h6>商品名: {product.name}</h6>
          <p className="text-muted">ASIN: {product.amazonAttributes?.asin} | SKU: {product.sku}</p>
        </div>
      </div>

      <table className="table table-bordered table-sm">
        <thead className="thead-light text-center">
          <tr>
            <th style={{ minWidth: 100 }}>日付</th>
            {visibleColumnDefs.map(col => (
              <th key={col.key} style={{ minWidth: col.minWidth }}>{col.label}<MallBadge feeKey={col.key} /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dailyKpis.map(({ date, kpis, dailyUnitCost, dailyUnitShippingCost }) => (
            <tr key={date}>
              <td className="text-center">{date}</td>
              {renderKpiCells(kpis, dailyUnitCost, dailyUnitShippingCost, dailyVisibleColumns)}
            </tr>
          ))}
        </tbody>
        <tfoot className="table-light fw-bold" style={{ position: 'sticky', bottom: 0 }}>
          <tr>
            <td className="text-center">合計</td>
            {renderKpiCells(totals, totals.unitCost, totals.unitShippingCost, dailyVisibleColumns)}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
